import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../../lib/api";
import { normalizeImageUrl } from "../../lib/imageUrl";
import PublicNav from "../../components/PublicNav";
import Footer from "../../components/Footer";
import NewsletterCTA from "../../components/NewsletterCTA";
import CalendarPage from "./CalendarPage";
import MediaEmbed, { detectVideo } from "../../components/MediaEmbed";
import MediaBlocksRenderer from "../../components/MediaBlocksRenderer";
import WaitlistModal from "../../components/WaitlistModal";
import { Warning, PlayCircle, Bell } from "@phosphor-icons/react";
import Seo, { buildTourSchema } from "../../components/Seo";

export default function TourDetail() {
  const { slug } = useParams();
  const [tour, setTour] = useState(null);
  const [instances, setInstances] = useState([]);
  const [view, setView] = useState("list");
  const [waitlistInstance, setWaitlistInstance] = useState(null);

  useEffect(() => {
    api.get(`/tours/${slug}`).then(r => {
      setTour(r.data);
      api.get(`/instances?tour_id=${r.data.id}&upcoming_only=true&active_only=true`).then(i => setInstances(i.data));
    }).catch(() => {});
  }, [slug]);

  if (!tour) return (
    <div className="public-shell"><PublicNav overHero /><div className="pt-40 text-center text-ms-stone">Loading…</div></div>
  );

  const heroVideo = detectVideo(tour.hero_video_url);

  return (
    <div className="public-shell">
      <Seo
        title={tour.title}
        description={tour.seo_meta_description || tour.short_description || `${tour.title} — an immersive mystery tour in St. Petersburg, FL.`}
        image={normalizeImageUrl(tour.hero_image)}
        url={`https://mysteryserved.com/tour/${tour.slug}`}
        type="article"
        schema={buildTourSchema(tour)}
      />
      <PublicNav overHero />

      {/* HERO */}
      <section className="relative h-[78vh] min-h-[560px] overflow-hidden bg-ms-cream">
        {heroVideo ? (
          <div className="absolute inset-0 scale-110"><MediaEmbed url={tour.hero_video_url} mode="hero" /></div>
        ) : (
          <img src={normalizeImageUrl(tour.hero_image)} alt={tour.title} className="absolute inset-0 w-full h-full object-cover" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-transparent to-black/55" />
        <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-ms-cream via-ms-cream/85 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 h-full flex items-end pb-28">
          <div className="max-w-3xl animate-fade-up">
            <p className="label-eyebrow text-ms-brass-hi" style={{textShadow:'0 1px 12px rgba(0,0,0,0.4)'}}>{tour.difficulty}</p>
            <h1 className="serif text-white text-5xl md:text-7xl mt-5 leading-[0.95]" style={{textShadow:'0 2px 24px rgba(0,0,0,0.35)'}}>{tour.title}</h1>
            {tour.tagline && <p className="serif italic text-2xl md:text-3xl text-ms-brass-hi mt-5" style={{textShadow:'0 1px 12px rgba(0,0,0,0.4)'}}>{tour.tagline}</p>}
          </div>
        </div>
      </section>

      {/* BODY */}
      <section className="bg-ms-cream py-20 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-7 space-y-14">
            <div>
              <p className="label-eyebrow text-ms-brass">The experience</p>
              <p className="mt-6 text-ms-ink text-lg leading-[1.75] whitespace-pre-line max-w-2xl">
                {tour.full_description || tour.short_description}
              </p>
            </div>

            {tour.intro_video_url && detectVideo(tour.intro_video_url) && (
              <div>
                <p className="label-eyebrow text-ms-brass flex items-center gap-2"><PlayCircle size={16} weight="duotone" /> Watch the trailer</p>
                <div className="mt-5 aspect-video w-full overflow-hidden rounded-sm border border-ms-edge bg-ms-ink-deep">
                  <MediaEmbed url={tour.intro_video_url} mode="trailer" />
                </div>
              </div>
            )}

            {tour.included_food && (
              <Section label="Included food">{tour.included_food}</Section>
            )}
            {tour.what_to_bring && (
              <Section label="What to bring">{tour.what_to_bring}</Section>
            )}

            {Array.isArray(tour.media_blocks) && tour.media_blocks.length > 0 && (
              <MediaBlocksRenderer blocks={tour.media_blocks} />
            )}
            {tour.custom_label_1 && tour.custom_value_1 && (
              <Section label={tour.custom_label_1}>{tour.custom_value_1}</Section>
            )}
            {tour.custom_label_2 && tour.custom_value_2 && (
              <Section label={tour.custom_label_2}>{tour.custom_value_2}</Section>
            )}
            {tour.cancellation_policy && (
              <Section label="Cancellation">{tour.cancellation_policy}</Section>
            )}

            {tour.faq?.length > 0 && (
              <div data-testid="tour-faq">
                <p className="label-eyebrow text-ms-brass">FAQ</p>
                <h2 className="serif text-ms-ink text-3xl md:text-4xl mt-3">Common questions</h2>
                <div className="mt-7 space-y-3">
                  {tour.faq.map((q, i) => (
                    <details key={i} className="group bg-ms-ivory border border-ms-edge rounded-sm p-5 open:border-ms-brass transition-colors" data-testid={`tour-faq-item-${i}`}>
                      <summary className="cursor-pointer serif text-lg md:text-xl text-ms-ink hover:text-ms-brass list-none flex items-center justify-between gap-4">
                        <span>{q.question}</span>
                        <span className="text-ms-brass text-xl group-open:rotate-45 transition-transform shrink-0">+</span>
                      </summary>
                      <p className="mt-4 text-ms-stone text-sm md:text-base leading-[1.75] whitespace-pre-line">{q.answer}</p>
                    </details>
                  ))}
                </div>
              </div>
            )}

            <div className="bg-ms-warn/10 border border-ms-warn/40 rounded-sm p-5 flex items-start gap-3">
              <Warning size={22} weight="duotone" className="text-ms-warn flex-shrink-0 mt-0.5" />
              <p className="text-ms-ink/85 text-sm leading-[1.7]">{tour.drinks_disclaimer}</p>
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="bg-ms-ivory border border-ms-edge rounded-sm p-7 sticky top-28 shadow-sm">
              <div className="flex items-baseline justify-between gap-3">
                <div>
                  <p className="label-eyebrow text-ms-brass">Reserve</p>
                  <p className="serif text-4xl text-ms-ink mt-2">${tour.base_price} <span className="text-base text-ms-stone">/ guest</span></p>
                </div>
                <div className="flex border border-ms-edge rounded-full overflow-hidden text-xs">
                  <button onClick={() => setView("list")} className={`px-3 py-1.5 label-eyebrow transition-colors ${view==="list" ? "bg-ms-ink text-ms-cream" : "text-ms-stone hover:text-ms-ink"}`} data-testid="view-list">List</button>
                  <button onClick={() => setView("calendar")} className={`px-3 py-1.5 label-eyebrow transition-colors ${view==="calendar" ? "bg-ms-ink text-ms-cream" : "text-ms-stone hover:text-ms-ink"}`} data-testid="view-calendar">Calendar</button>
                </div>
              </div>
              <div className="brass-line my-6" />
              {view === "list" ? (
                instances.length === 0 ? (
                  <p className="text-ms-stone text-sm">No upcoming dates yet.</p>
                ) : (
                  <div className="space-y-3 max-h-[60vh] overflow-y-auto pr-1">
                    {instances.map(inst => {
                      const left = inst.capacity - (inst.booked_count || 0);
                      const dt = new Date(inst.starts_at);
                      if (left <= 0) {
                        return (
                          <button
                            key={inst.id}
                            type="button"
                            onClick={() => setWaitlistInstance(inst)}
                            className="w-full flex items-center justify-between border border-ms-edge rounded-sm p-4 hover:border-ms-brass hover:bg-ms-cream transition-all text-left"
                            data-testid={`tour-instance-waitlist-${inst.id}`}
                          >
                            <div>
                              <p className="text-ms-ink text-sm">{dt.toLocaleDateString(undefined, {weekday:'short', month:'short', day:'numeric'})}</p>
                              <p className="text-ms-stone text-xs mt-0.5">{dt.toLocaleTimeString([], {hour:'numeric', minute:'2-digit'})} · Sold out</p>
                            </div>
                            <span className="label-eyebrow text-ms-brass text-[10px] inline-flex items-center gap-1">
                              <Bell size={11} weight="duotone" /> Waitlist
                            </span>
                          </button>
                        );
                      }
                      return (
                        <Link key={inst.id} to={`/checkout/${inst.id}`}
                          className="flex items-center justify-between border border-ms-edge rounded-sm p-4 hover:border-ms-brass hover:bg-ms-cream transition-all"
                          data-testid={`tour-instance-${inst.id}`}
                        >
                          <div>
                            <p className="text-ms-ink text-sm">{dt.toLocaleDateString(undefined, {weekday:'short', month:'short', day:'numeric'})}</p>
                            <p className="text-ms-stone text-xs mt-0.5">{dt.toLocaleTimeString([], {hour:'numeric', minute:'2-digit'})}</p>
                          </div>
                          <span className="label-eyebrow text-ms-brass text-[10px]">{left} left →</span>
                        </Link>
                      );
                    })}
                  </div>
                )
              ) : (
                <div className="-mx-2"><CalendarPage compact tourId={tour.id} tour={tour} /></div>
              )}

              {/* Gift this experience — secondary CTA below the date picker */}
              <div className="mt-6 pt-5 border-t border-ms-edge" data-testid="gift-this-tour">
                <Link
                  to={`/gift-cards?tour=${tour.slug}`}
                  className="group flex items-center justify-between text-ms-ink hover:text-ms-brass transition-colors"
                  data-testid="gift-this-tour-link"
                >
                  <span className="flex flex-col">
                    <span className="label-eyebrow text-ms-stone text-[10px]">Not for you?</span>
                    <span className="text-sm mt-0.5">Gift this experience</span>
                  </span>
                  <span className="text-ms-brass group-hover:translate-x-1 transition-transform">→</span>
                </Link>
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* GALLERY */}
      {tour.gallery?.length > 0 && (
        <section className="bg-parchment py-20 md:py-28">
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <p className="label-eyebrow text-ms-brass">A glimpse</p>
            <h2 className="serif text-ms-ink text-3xl md:text-4xl mt-3 mb-10">From past tours.</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
              {tour.gallery.map((g, i) => {
                const isVideo = detectVideo(g);
                const cls = i === 0 ? "md:col-span-2 md:row-span-2 aspect-square md:aspect-[4/3]" : "aspect-square";
                return (
                  <div key={i} className={`${cls} overflow-hidden bg-ms-clay rounded-sm`}>
                    {isVideo
                      ? <MediaEmbed url={g} mode="trailer" className="w-full h-full" />
                      : <img src={g} alt="" className="w-full h-full object-cover hover:scale-[1.03] transition-transform duration-700" />}
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      <NewsletterCTA />
      <Footer />
      {waitlistInstance && (
        <WaitlistModal
          instance={waitlistInstance}
          tour={tour}
          onClose={() => setWaitlistInstance(null)}
        />
      )}
    </div>
  );
}

function Section({ label, children }) {
  return (
    <div>
      <p className="label-eyebrow text-ms-brass">{label}</p>
      <p className="mt-5 text-ms-ink/90 text-base leading-[1.75] whitespace-pre-line max-w-2xl">{children}</p>
    </div>
  );
}
